function bindConsoleMethod(name: "log" | "debug" | "info" | "warn" | "error") {
  try {
    const c = (window as any).console
    const fn = c?.[name]
    return typeof fn === "function" ? fn.bind(c) : undefined
  } catch {
    return undefined
  }
}

const originalConsole = {
  log: bindConsoleMethod("log"),
  debug: bindConsoleMethod("debug"),
  info: bindConsoleMethod("info"),
  warn: bindConsoleMethod("warn"),
  error: bindConsoleMethod("error"),
}
type LogLevel = "DEBUG" | "INFO" | "WARNING" | "ERROR" | "CRITICAL"

type LogEvent = {
  level: LogLevel
  message: string
  category?: string
  meta?: Record<string, unknown>
  timestamp: number
}

type LoggerOptions = {
  environment: "development" | "production" | string
  threshold?: LogLevel
  dedupWindowMs?: number
  enableConsoleForward?: boolean
}

const levelOrder: Record<LogLevel, number> = {
  DEBUG: 10,
  INFO: 20,
  WARNING: 30,
  ERROR: 40,
  CRITICAL: 50,
}

function defaultThreshold(env: string): LogLevel {
  return env === "development" ? "DEBUG" : "WARNING"
}

function stringifyArgs(args: unknown[]): string {
  try {
    return args
      .map((a) => {
        if (a instanceof Error) {
          const name = a.name || "Error"
          const msg = a.message || String(a)
          const stack = a.stack ? `\n${a.stack}` : ""
          return `${name}: ${msg}${stack}`
        }
        if (typeof a === "object" && a && "message" in (a as any)) {
          const m = (a as any).message
          return typeof m === "string" ? m : JSON.stringify(a)
        }
        return typeof a === "string" ? a : JSON.stringify(a)
      })
      .join(" ")
  } catch {
    return args.map((a) => {
      try {
        return String(a)
      } catch {
        return "[Unserializable argument]"
      }
    }).join(" ")
  }
}

export class Logger {
  private env: string
  private threshold: LogLevel
  private dedupWindowMs: number
  private enableConsoleForward: boolean

  private receivedCount: Record<LogLevel, number> = {
    DEBUG: 0,
    INFO: 0,
    WARNING: 0,
    ERROR: 0,
    CRITICAL: 0,
  }
  private emittedCount: Record<LogLevel, number> = {
    DEBUG: 0,
    INFO: 0,
    WARNING: 0,
    ERROR: 0,
    CRITICAL: 0,
  }
  private suppressedCount: Record<LogLevel, number> = {
    DEBUG: 0,
    INFO: 0,
    WARNING: 0,
    ERROR: 0,
    CRITICAL: 0,
  }

  private auditLogKey = "audit_logs"
  private auditMaxEntries = 1000
  private auditRetentionDays = 7

  private dedupMap: Map<string, { count: number; lastTs: number }> = new Map()
  private consoleOriginal = originalConsole

  constructor(options: LoggerOptions) {
    this.env = options.environment
    this.threshold = options.threshold ?? defaultThreshold(options.environment)
    this.dedupWindowMs = options.dedupWindowMs ?? 60_000
    this.enableConsoleForward = options.enableConsoleForward ?? true

    if (this.env === "production") {
      // Periodically print suppression summary in production
      setInterval(() => this.printSuppressionSummary(), 60_000)
    }
  }

  setThreshold(level: LogLevel) {
    this.threshold = level
  }

  private shouldEmit(level: LogLevel, category?: string): boolean {
    // Always emit security events
    if (category === "security") return true
    return levelOrder[level] >= levelOrder[this.threshold]
  }

  private dedupKey(level: LogLevel, msg: string, category?: string): string {
    return `${level}|${category ?? "general"}|${msg}`
  }

  private handleDedup(level: LogLevel, key: string, now: number): boolean {
    const entry = this.dedupMap.get(key)
    if (!entry) {
      this.dedupMap.set(key, { count: 0, lastTs: now })
      return false // not duplicated yet
    }
    if (now - entry.lastTs < this.dedupWindowMs) {
      entry.count += 1
      this.suppressedCount[level] += 1
      return true // suppress duplicate within window
    }
    // Window passed — reset
    entry.count = 0
    entry.lastTs = now
    return false
  }

  private safeConsole() {
    const c = (this.consoleOriginal as any) || (window as any).console || {}
    const native = (window as any).console || {}
    return {
      debug: typeof c.debug === "function" ? c.debug.bind(c) : typeof native.debug === "function" ? native.debug.bind(native) : undefined,
      info: typeof c.info === "function" ? c.info.bind(c) : typeof native.info === "function" ? native.info.bind(native) : undefined,
      warn: typeof c.warn === "function" ? c.warn.bind(c) : typeof native.warn === "function" ? native.warn.bind(native) : undefined,
      error: typeof c.error === "function" ? c.error.bind(c) : typeof native.error === "function" ? native.error.bind(native) : undefined,
    }
  }

  private forwardToConsole(ev: LogEvent) {
    if (!this.enableConsoleForward) return
    const msg = `[${ev.level}]${ev.category ? `(${ev.category})` : ""} ${ev.message}`
    const c = this.safeConsole()
    switch (ev.level) {
      case "DEBUG":
        c.debug?.(msg, ev.meta ?? {})
        break
      case "INFO":
        c.info?.(msg, ev.meta ?? {})
        break
      case "WARNING":
        c.warn?.(msg, ev.meta ?? {})
        break
      case "ERROR":
        c.error?.(msg, ev.meta ?? {})
        break
      case "CRITICAL":
        c.error?.(msg, ev.meta ?? {})
        break
    }
  }

  private writeAudit(ev: LogEvent) {
    if (ev.category !== "security") return
    try {
      const raw = localStorage.getItem(this.auditLogKey)
      const arr: LogEvent[] = raw ? JSON.parse(raw) : []
      // purge old
      const cutoff = Date.now() - this.auditRetentionDays * 24 * 60 * 60 * 1000
      const filtered = arr.filter((e) => e.timestamp >= cutoff)
      filtered.push(ev)
      // trim
      if (filtered.length > this.auditMaxEntries) {
        filtered.splice(0, filtered.length - this.auditMaxEntries)
      }
      localStorage.setItem(this.auditLogKey, JSON.stringify(filtered))
    } catch {
      // ignore storage failures
    }
  }

  private printSuppressionSummary() {
    const totalSuppressed = Object.values(this.suppressedCount).reduce((a, b) => a + b, 0)
    if (totalSuppressed === 0) return
    const summary = {
      suppressed: this.suppressedCount,
      emitted: this.emittedCount,
      received: this.receivedCount,
      reductionPercent: this.reductionPercent(),
    }
    this.safeConsole().info?.("[LOG] Suppression summary:", summary)
  }

  private emit(ev: LogEvent) {
    this.emittedCount[ev.level] += 1
    this.forwardToConsole(ev)
    this.writeAudit(ev)
  }

  log(level: LogLevel, message: string, opts?: { category?: string; meta?: Record<string, unknown> }) {
    const now = Date.now()
    this.receivedCount[level] += 1
    const ev: LogEvent = { level, message, category: opts?.category, meta: opts?.meta, timestamp: now }

    // Filtering by threshold
    if (!this.shouldEmit(level, ev.category)) {
      this.suppressedCount[level] += 1
      return
    }
    // Dedup within window for non-critical
    const key = this.dedupKey(level, message, ev.category)
    const isDup = level !== "CRITICAL" && this.handleDedup(level, key, now)
    if (isDup) return
    this.emit(ev)
  }

  debug(msg: string, opts?: { category?: string; meta?: Record<string, unknown> }) {
    this.log("DEBUG", msg, opts)
  }
  info(msg: string, opts?: { category?: string; meta?: Record<string, unknown> }) {
    this.log("INFO", msg, opts)
  }
  warning(msg: string, opts?: { category?: string; meta?: Record<string, unknown> }) {
    this.log("WARNING", msg, opts)
  }
  error(msg: string, opts?: { category?: string; meta?: Record<string, unknown> }) {
    this.log("ERROR", msg, opts)
  }
  critical(msg: string, opts?: { category?: string; meta?: Record<string, unknown> }) {
    this.log("CRITICAL", msg, opts)
  }

  // Patch console to route through the logger
  patchConsole() {
    const self = this
    console.debug = (...args: unknown[]) => self.debug(stringifyArgs(args), { category: "console" })
    console.info = (...args: unknown[]) => self.info(stringifyArgs(args), { category: "console" })
    console.log = (...args: unknown[]) => self.info(stringifyArgs(args), { category: "console" })
    console.warn = (...args: unknown[]) => self.warning(stringifyArgs(args), { category: "console" })
    console.error = (...args: unknown[]) => self.error(stringifyArgs(args), { category: "console" })
  }

  metrics() {
    const totalReceived = Object.values(this.receivedCount).reduce((a, b) => a + b, 0)
    const totalEmitted = Object.values(this.emittedCount).reduce((a, b) => a + b, 0)
    const totalSuppressed = Object.values(this.suppressedCount).reduce((a, b) => a + b, 0)
    return {
      received: this.receivedCount,
      emitted: this.emittedCount,
      suppressed: this.suppressedCount,
      totals: { received: totalReceived, emitted: totalEmitted, suppressed: totalSuppressed },
      reductionPercent: this.reductionPercent(),
    }
  }

  reductionPercent(): number {
    const totals = Object.values(this.receivedCount).reduce((a, b) => a + b, 0)
    const emitted = Object.values(this.emittedCount).reduce((a, b) => a + b, 0)
    if (totals === 0) return 0
    return Math.round(((totals - emitted) / totals) * 10000) / 100 // 2 decimal places
  }
}

export function createLogger(options?: Partial<LoggerOptions>) {
  const env = (import.meta as any).env?.MODE ?? "development"
  const logger = new Logger({ environment: env, threshold: options?.threshold, dedupWindowMs: options?.dedupWindowMs, enableConsoleForward: options?.enableConsoleForward })
  return logger
}
